/* ═══════════════════════════════════════════════════════════════
   The Designer — the paragraph context menu.

   Two absolute rules of the design law meet here:
     · every feature is reachable from the visible UI — nothing is
       keyboard-only, because the user is an architect, not a developer
     · wherever the app can copy or paste, it offers copy AND cut AND
       paste, each reachable BOTH by menu and by shortcut

   So right-clicking a paragraph offers the full clipboard triad, the
   inline text controls Word users expect (bold / italic / underline /
   highlight), and the spec operations that Tab and Shift+Tab do from
   the keyboard.

   R5: "Level style…" lives here too. Level styling is a sub-tool, not a
   mode — this menu and the Levels palette are its two entry points, and
   clicking body text is neither of them.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import type { Editor } from '@tiptap/react';
import { NodeSelection } from '@tiptap/pm/state';
import {
  deleteSelectedBlocks,
  duplicateSelectedBlocks,
  insertImageBlock,
  insertTableBlock,
  pickImageFile,
  setLevel,
  toggleExcluded,
} from './commands';
import { TableMenuSection } from './TableMenuSection';
import { moduleStampLabel, type ModuleStamp } from '../../shared/spec-modules';
import { rem } from '../lib/rem';
import { inTable } from './tableCommands';
import {
  ContextMenu,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from '../components/ui/context-menu';

export interface BlockMenuState {
  x: number;
  y: number;
  level: number;
  excluded: boolean;
  /** Phase 5 — which block was right-clicked, and the module insertion it came
   *  from (null for text somebody typed here). Read from the node's own data,
   *  never from a class, so the menu can only act on a real insertion. */
  blockId: string;
  moduleStamp: ModuleStamp | null;
  /** Resolved before the menu opens (probe, 80ms cap); false hides Paste. */
  clipboardHas?: boolean;
}

/** Acting on the whole INSERTION a block belongs to (Phase 5). Absent when the
 *  host has not wired the module flow. */
export interface BlockModuleMenuProps {
  selectInsertion: (insertId: string, blockId: string) => void;
  removeInsertion: (stamp: ModuleStamp) => void;
}

interface Props {
  editor: Editor;
  state: BlockMenuState;
  onClose: () => void;
  canEdit: boolean;
  /** Point the Levels palette at this paragraph's level and open it. */
  onLevelStyle?: (level: number) => void;
  moduleMenu?: BlockModuleMenuProps | null;
  /** Insert a Related sections block below — the host seeds it with the
   *  project's sections. Absent → the item is not offered. */
  onInsertRelatedSections?: () => void;
}

const MENU_W = rem(232);

/** The four character styles that exist. The forma owns everything else, so
 *  there is deliberately no font, size or colour path. */
const MARKS: Array<{ name: string; label: string; key: string }> = [
  { name: 'bold', label: 'Bold', key: 'Ctrl+B' },
  { name: 'italic', label: 'Italic', key: 'Ctrl+I' },
  { name: 'underline', label: 'Underline', key: 'Ctrl+U' },
  { name: 'highlight', label: 'Highlight', key: 'Ctrl+Alt+H' },
];
const LEVEL_NAMES = [
  'Level 1 — PART',
  'Level 2 — 1.01',
  'Level 3 — A.',
  'Level 4 — 1.',
  'Level 5 — a.',
  'Level 6 — 1)',
  'Level 7 — a)',
  'Level 8 — (1)',
  'Level 9 — (a)',
];

export function BlockMenu({
  editor,
  state,
  onClose,
  canEdit,
  onLevelStyle,
  moduleMenu,
  onInsertRelatedSections,
}: Props) {
  const dispatch = editor.view.dispatch.bind(editor.view);
  const clipboardHas = state.clipboardHas !== false;
  const tableHere = inTable(editor.state);

  // A picked ATOM (a figure, a Related sections block) has no DOM text
  // selection the browser can copy or cut from a menu. The document takes
  // focus back so ProseMirror's own copy serializes the block; a cut is that
  // copy plus the same whole-block delete Del does (the browser never offers
  // a native cut on a non-editable node).
  const atomPicked = () => editor.state.selection instanceof NodeSelection;
  const copy = () => {
    if (atomPicked()) editor.view.focus();
    document.execCommand('copy');
  };
  const cut = () => {
    if (!atomPicked()) {
      document.execCommand('cut');
      return;
    }
    editor.view.focus();
    if (document.execCommand('copy')) {
      deleteSelectedBlocks(editor.state, dispatch);
    }
  };
  const paste = async () => {
    try {
      // A copied smart block (Related sections) comes back as the block it
      // was — the editor's own HTML paste rebuilds it with all its params.
      // Anything else pastes as plain text, exactly as it always has.
      for (const item of await navigator.clipboard.read()) {
        if (!item.types.includes('text/html')) continue;
        const html = await (await item.getType('text/html')).text();
        if (html.includes('data-smart-block')) {
          editor.view.focus();
          editor.view.pasteHTML(html);
          return;
        }
      }
    } catch {
      // No rich clipboard access — fall through to the plain-text paste.
    }
    try {
      const text = await navigator.clipboard.readText();
      if (text) editor.chain().focus().insertContent(text).run();
    } catch {
      // Clipboard permission denied — Ctrl+V still works, the browser
      // handles it natively without going through this path.
    }
  };

  const blockItems = () => (
    <>
      <ContextMenuItem className="dsg-menu-item" onSelect={copy}>
        Copy<span className="dsg-menu-key">Ctrl+C</span>
      </ContextMenuItem>
      <ContextMenuItem className="dsg-menu-item" onSelect={cut} disabled={!canEdit}>
        Cut<span className="dsg-menu-key">Ctrl+X</span>
      </ContextMenuItem>
      {clipboardHas && (
        <ContextMenuItem
          className="dsg-menu-item"
          onSelect={() => void paste()}
          disabled={!canEdit}
        >
          Paste<span className="dsg-menu-key">Ctrl+V</span>
        </ContextMenuItem>
      )}

      <ContextMenuSeparator className="dsg-menu-sep" />

      <ContextMenuItem
        className="dsg-menu-item"
        onSelect={() => duplicateSelectedBlocks(editor.state, dispatch)}
        disabled={!canEdit}
        title="Insert a copy of this paragraph right below it"
      >
        Duplicate<span className="dsg-menu-key">Ctrl+D</span>
      </ContextMenuItem>

      {/* ── Insert below ── */}
      <ContextMenuSub>
        <ContextMenuSubTrigger className="dsg-menu-item" disabled={!canEdit}>
          Insert<span className="dsg-menu-key">▸</span>
        </ContextMenuSubTrigger>
        <ContextMenuSubContent className="dsg-menu" style={{ width: MENU_W }}>
          <ContextMenuItem
            className="dsg-menu-item"
            onSelect={() => {
              // The picker outlives the menu; the insert lands when it resolves.
              void pickImageFile().then((img) => {
                if (img) {
                  insertImageBlock(editor.state, dispatch, img.dataUrl, img.name);
                }
              });
            }}
            disabled={!canEdit}
            title="Insert an image below this paragraph, at print width"
          >
            Insert image
          </ContextMenuItem>
          <ContextMenuItem
            className="dsg-menu-item"
            onSelect={() => insertTableBlock(editor.state, dispatch)}
            disabled={!canEdit}
            title="Insert a 3×3 table below this paragraph"
          >
            Insert table
          </ContextMenuItem>
          {onInsertRelatedSections && (
            <ContextMenuItem
              className="dsg-menu-item"
              onSelect={() => onInsertRelatedSections()}
              disabled={!canEdit}
              title="Insert a list of this project's other sections below this paragraph"
            >
              Insert related sections
            </ContextMenuItem>
          )}
        </ContextMenuSubContent>
      </ContextMenuSub>

      {/* ── Change level (Tab) ── */}
      <ContextMenuSub>
        <ContextMenuSubTrigger className="dsg-menu-item" disabled={!canEdit}>
          Change level<span className="dsg-menu-key">Tab</span>
        </ContextMenuSubTrigger>
        <ContextMenuSubContent className="dsg-menu" style={{ width: MENU_W }}>
          {LEVEL_NAMES.map((name, i) => (
            <ContextMenuItem
              key={i}
              className={`dsg-menu-item${state.level === i + 1 ? ' is-current' : ''}`}
              aria-current={state.level === i + 1 ? 'true' : undefined}
              onSelect={() => setLevel(editor.state, dispatch, i + 1)}
            >
              {name}
              {state.level === i + 1 && <span className="dsg-menu-key">Current</span>}
            </ContextMenuItem>
          ))}
        </ContextMenuSubContent>
      </ContextMenuSub>

      {/* ── Inline text controls — direct formatting, additive over the level
          style, and a different thing entirely from revision marks. ── */}
      <ContextMenuSub>
        <ContextMenuSubTrigger className="dsg-menu-item" disabled={!canEdit}>
          Format<span className="dsg-menu-key">▸</span>
        </ContextMenuSubTrigger>
        <ContextMenuSubContent className="dsg-menu" style={{ width: MENU_W }}>
          {MARKS.map((m) => (
            <ContextMenuCheckboxItem
              key={m.name}
              checked={editor.isActive(m.name)}
              className={`dsg-menu-item${editor.isActive(m.name) ? ' is-current' : ''}`}
              onCheckedChange={() => editor.chain().focus().toggleMark(m.name).run()}
              disabled={!canEdit}
            >
              {m.label}
              <span className="dsg-menu-key">{m.key}</span>
            </ContextMenuCheckboxItem>
          ))}
          {onLevelStyle && (
            <ContextMenuItem
              className="dsg-menu-item"
              onSelect={() => onLevelStyle(state.level)}
              disabled={!canEdit}
              title={`Open the Levels palette on level ${state.level} — restyles every level ${state.level} paragraph`}
            >
              Level style…
              <span className="dsg-menu-key">L{state.level}</span>
            </ContextMenuItem>
          )}
        </ContextMenuSubContent>
      </ContextMenuSub>

      {/* ── More: export skip + the module insertion this block belongs to
          (Phase 5). A project spec is a hand-managed copy: removing an
          insertion here deletes blocks from THIS spec and nothing else. ── */}
      <ContextMenuSub>
        <ContextMenuSubTrigger className="dsg-menu-item">
          More<span className="dsg-menu-key">▸</span>
        </ContextMenuSubTrigger>
        <ContextMenuSubContent className="dsg-menu" style={{ width: MENU_W }}>
          <ContextMenuCheckboxItem
            checked={state.excluded}
            className={`dsg-menu-item${state.excluded ? ' is-current' : ''}`}
            onCheckedChange={() => toggleExcluded(editor.state, dispatch)}
            disabled={!canEdit}
            title="Keep the paragraph here but leave it out of the exported document"
          >
            {state.excluded ? 'Include in export' : 'Skip at export'}
          </ContextMenuCheckboxItem>
          {state.moduleStamp && moduleMenu && (
            <>
              <ContextMenuSeparator className="dsg-menu-sep" />
              <ContextMenuLabel className="dsg-menu-label">
                {moduleStampLabel(state.moduleStamp)}
              </ContextMenuLabel>
              <ContextMenuItem
                className="dsg-menu-item"
                onSelect={() =>
                  moduleMenu.selectInsertion(state.moduleStamp!.insertId, state.blockId)
                }
                title="Select the blocks this insertion brought in"
              >
                Select insertion
              </ContextMenuItem>
              <ContextMenuItem
                className="dsg-menu-item is-danger"
                onSelect={() => moduleMenu.removeInsertion(state.moduleStamp!)}
                disabled={!canEdit}
                title="Delete every block this insertion brought in — Ctrl+Z brings them back"
              >
                Remove insertion
              </ContextMenuItem>
            </>
          )}
        </ContextMenuSubContent>
      </ContextMenuSub>

      <ContextMenuSeparator className="dsg-menu-sep" />
      <ContextMenuItem
        className="dsg-menu-item is-danger"
        onSelect={() => deleteSelectedBlocks(editor.state, dispatch)}
        disabled={!canEdit}
        title="Delete this paragraph — Ctrl+Z brings it back"
      >
        Delete<span className="dsg-menu-key">Del</span>
      </ContextMenuItem>
    </>
  );

  return (
    <ContextMenu open onOpenChange={(next) => !next && onClose()}>
      {/* Invisible fixed anchor at the click point, portalled to <body> so a
          transformed ancestor (zoomed canvas, dock panel) cannot shift it —
          the same pattern as the Spec Studio block menu. */}
      {createPortal(
        <ContextMenuTrigger asChild>
          <span
            aria-hidden="true"
            style={{
              position: 'fixed',
              left: state.x,
              top: state.y,
              width: rem(1),
              height: rem(1),
              pointerEvents: 'none',
            }}
          />
        </ContextMenuTrigger>,
        document.body
      )}
      <ContextMenuContent
        className="dsg-menu"
        style={{
          width: MENU_W,
          maxHeight: `min(${rem(420)}, var(--radix-dropdown-menu-content-available-height, ${rem(420)}))`,
        }}
        align="start"
        sideOffset={0}
        collisionPadding={8}
        aria-label="Paragraph actions"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {/* Right-clicking inside a table: Word's table commands ARE the
            context, top-level; every paragraph action steps down into Block. */}
        {tableHere ? (
          <>
            <TableMenuSection editor={editor} canEdit={canEdit} onClose={onClose} radixItems bare />
            <ContextMenuSeparator className="dsg-menu-sep" />
            <ContextMenuSub>
              <ContextMenuSubTrigger className="dsg-menu-item">
                Block<span className="dsg-menu-key">▸</span>
              </ContextMenuSubTrigger>
              <ContextMenuSubContent className="dsg-menu" style={{ width: MENU_W }}>
                {blockItems()}
              </ContextMenuSubContent>
            </ContextMenuSub>
          </>
        ) : (
          blockItems()
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}
